/**
 * A person's name as the app shows it: "Last First" (#479).
 *
 * The operator's decision of 2026-09-25: every list, page, picker, print
 * view, confirmation and notification shows "Jonaitis Jonas". The sorting
 * stays by the last name. Three places keep their own layout: the Ring TV
 * and the bout corners put the last name in large letters above the first
 * name, and the avatar shows the initials.
 *
 * The API returns the parts (`first_name`, `last_name`, `red_first_name`,
 * ...), never a joined name. `backend/src/lib/personName.ts` holds the same
 * `personName()` for the texts that the server writes: notifications,
 * warnings and audit labels. `e2e/tests/person-name-parity-479.spec.ts`
 * gives both files the same inputs, and `person-name-census-479.spec.ts`
 * fails when code outside these files joins the parts by hand.
 *
 * The file has no runtime import (the one `import type` is erased), so a
 * pure spec can load it.
 */
import type { BoutCornerColour } from './boutCorner';

/** One part of a name, trimmed. A value that is not a string is empty. */
function part(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * "Last First", for example "Jonaitis Jonas". Each part is trimmed, and an
 * empty part is left out: a one-word Google name gives "Jonas", and two
 * empty parts give ''.
 */
export function personName(first?: string | null, last?: string | null): string {
  return [part(last), part(first)].filter(Boolean).join(' ');
}

/** The name parts of the two corners, as a bout row carries them. */
export interface CornerNameParts {
  red_first_name?: string | null;
  red_last_name?: string | null;
  blue_first_name?: string | null;
  blue_last_name?: string | null;
}

/** The name of one corner of a bout row: `personName()` of its parts. */
export function cornerName(bout: CornerNameParts | null | undefined, corner: BoutCornerColour): string {
  if (!bout) return '';
  return corner === 'red'
    ? personName(bout.red_first_name, bout.red_last_name)
    : personName(bout.blue_first_name, bout.blue_last_name);
}

/**
 * The search form of a text: lower case, with no accent mark. "Jokūbas" and
 * "Žilinskas" give "jokubas" and "zilinskas". NFD splits a Lithuanian letter
 * into its base letter and a combining mark, and the mark goes.
 */
function fold(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/**
 * The name search of every list. True when each word of the query is in
 * the name, in either order, in any case and with or without the accent
 * marks: "jonas jon" and "Jonaitis J" both find Jonaitis Jonas, and
 * "Jokubas" finds Jokūbas. An empty query finds every name.
 *
 * The accents (2026-10-08): a coach typed "Jokubas", did not find the
 * record "Jokūbas", and made a second record of the same boxer. His bouts
 * are now on two records.
 */
export function nameMatches(query: string | null | undefined, first?: string | null, last?: string | null): boolean {
  const name = fold(personName(first, last));
  return fold(part(query)).split(/\s+/).filter(Boolean).every(word => name.includes(word));
}

/**
 * The search of a list for the fields that are not a person name: a team, an
 * email, a location, the label of a pick list (#661, F30). True when each
 * word of the query is in at least one field, in any case and with or
 * without the accent marks: "zalgiris" finds the team "Žalgiris". The query
 * is trimmed, so the space that a phone keyboard adds after a word does not
 * empty the list. An empty query finds every row.
 *
 * A row search of people is `nameMatches(q, first, last) || textMatches(q, team)`.
 */
export function textMatches(query: string | null | undefined, ...fields: (string | null | undefined)[]): boolean {
  const words = fold(part(query)).split(/\s+/).filter(Boolean);
  const haystacks = fields.map(f => fold(part(f)));
  return words.every(word => haystacks.some(h => h.includes(word)));
}

/** The name parts of a row that a list sorts by person. */
export interface NameParts {
  first_name?: string | null;
  last_name?: string | null;
}

/** One collator for every sort of a person list. The names are Lithuanian:
 *  the `lt` rules put Č, Š and Ž after C, S and Z and "Y" between I and J, on
 *  every device and in every UI language. */
const nameCollator = new Intl.Collator('lt');

/** The sort of a person list: the last name, then the first name (#661, F30). */
export function compareNames(a: NameParts, b: NameParts): number {
  return nameCollator.compare(part(a.last_name), part(b.last_name))
    || nameCollator.compare(part(a.first_name), part(b.first_name));
}
